import { Component, OnDestroy } from '@angular/core';
import { BaseClass } from 'src/app/shared/classes/base';
import { SubordinateGoalStatus } from '../goals/_interfaces/subordinate-goals-status.enum';
import { Subject } from 'rxjs';
import { LoadingController, ToastController } from '@ionic/angular';
import { TranslationService } from 'src/app/shared/services/translation.service';
import { AuthService } from 'src/app/shared/services/auth.service';
import { ActivatedRoute, Router } from '@angular/router';
import { GoalsService } from 'src/app/goals/goals.service';
import { UtilService } from 'src/app/shared/services/util.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { SubordinateFeedbacks } from 'src/app/final-feedback-validation/interfaces/subordinate-feedbacks.interface';
import { SubordinateGoalPreview } from 'src/app/goals/_interfaces/subordinate-goals-preview.interface';
import { FinalFeedbackService } from 'src/app/final-feedback/final-feedback.service';
import { FinalFeedbackStatus } from 'src/app/final-feedback/interfaces/final-feedback.interface';

@Component({
    selector: 'app-final-feedback-validation',
    templateUrl: './final-feedback-validation.page.html',
})
export class FinalFeedbackValidationPage extends BaseClass implements OnDestroy  {
    public isAllowedToReopen: boolean = false;
    public processTitle: string;
    public language = this._translationService.getSelectedLanguage();
    public SubordinateStatus = FinalFeedbackStatus;
    public subordinates: Array<SubordinateFeedbacks>;
    public displayedSubordinates: Array<SubordinateFeedbacks>;

    private _userRoles: Array<string> = [];
    private readonly _cycleId: number;
    private readonly _cycleProcessConfigId: number;
    private _searchSubject: Subject<string> = new Subject();

    constructor(
            protected _toastController: ToastController,
            protected _translationService: TranslationService,
            private _loadingController: LoadingController,
            private _authService: AuthService,
            private _router: Router,
            private _goalsService: GoalsService,
            private _activatedRoute: ActivatedRoute,
            private _utilService: UtilService,
            private _finalFeedbackService: FinalFeedbackService
    ) {
        super(_toastController, _translationService);

        this._cycleId = parseInt(
                this._activatedRoute.snapshot.paramMap.get('cycleId'), 10
        );
        this._cycleProcessConfigId = parseInt(
                this._activatedRoute.snapshot.paramMap.get('cycleProcessConfigId'), 10
        );
        this._userRoles = this._authService.getUserRoles();
        if (this._userRoles.includes('Administrator') ||
                this._userRoles.includes('HR')) {
            this.isAllowedToReopen = true;
        }
        this._setSearchSubscription();
    }

    ionViewWillEnter() {
        this._loadSubordinates(this._cycleId);
    }

    private async _loadSubordinates(cycleId: number): Promise<void> {
        const loading = await this._loadingController.create();
        await loading.present();

        this._finalFeedbackService.getSubordinatesForValidation(
                this._cycleProcessConfigId
        ).subscribe((response) => {
            this.subordinates = response.data.users;
            this.displayedSubordinates = response.data.users;
            this.processTitle = response.data.globalizationConfig ? response.data.globalizationConfig[this.language].title:'';
            loading.dismiss();

        }, (error) => {
            loading.dismiss();
            this.getErrorNotification(error);
        });
    }

    public goBack(): void {
        this._utilService.removeProcessIdFromLocalStorage();
        this._router.navigate(['dashboard']);
    }

    private _setReturnPath(storageKey: string): void {
        localStorage.setItem(storageKey, "final-feedback-validation/" + this._cycleId + "/" + this._cycleProcessConfigId);
    }

    ionViewDidLeave() {
        this._utilService.removeProcessIdFromLocalStorage();
    }

    public goToValidation(user: SubordinateFeedbacks): void {
        this._setReturnPath('FinalFeedbackReview_ReturnPath');
        this._router.navigate([user.id], {relativeTo: this._activatedRoute});
    }

    public getStatusColor(status: SubordinateGoalStatus): string {
        switch (status) {
            case SubordinateGoalStatus.Validated:
                return 'success';
            case SubordinateGoalStatus.WaitingChanges:
                return 'warning';
            case SubordinateGoalStatus.Pending:
            case SubordinateGoalStatus.WaitingValidation:
            default:
                return 'primary';
        }
    }

    public updateSearch(searchTextValue: string) {
        this._searchSubject.next(searchTextValue);
    }

    private _setSearchSubscription() {
        this._searchSubject.pipe(
                distinctUntilChanged(),
                debounceTime(1000)
        ).subscribe((searchValue: string) => {
            this.displayedSubordinates = this.subordinates.filter(goal => {
                return goal.name.toLowerCase().includes(
                        searchValue.toLowerCase().trim()
                );
            });
        });
    }
    

    public reopenValidation(user: SubordinateFeedbacks): void {
        this._finalFeedbackService.reopenFinalFeedback(
                user.cycleParticipantId, this._cycleProcessConfigId
        ).subscribe((response) => {
                    this._loadSubordinates(this._cycleId);
                    this.notifySuccess('performanceCycles.processReopened');
                }
                , (error) => this.notifyError(error));
    }

    ngOnDestroy() {
        this._searchSubject.unsubscribe();
    }

}